import type { FC } from 'react';
import React, { useCallback } from 'react';
import { styled, cssMap } from '@compiled/react';

import { token } from '@atlaskit/tokens';
import { Box, Inline, Stack } from '@atlaskit/primitives/compiled';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { FeatureCode } from '@confluence/feature-codes/entry-points/FeatureCodeModel';

import {
	ContentIcon,
	Subtitle,
	LastAction,
	StatusLozenge,
	Title,
	SocialProof,
	Excerpt,
	PageLink,
	PageCardContainer,
	AutomationDiscoveryMessage,
	AudioPlaylistButton,
} from '../Items';

const styles = cssMap({
	container: {
		width: '400px',
	},
	headerStyle: {
		width: '100%',
		overflowWrap: 'break-word',
		wordBreak: 'break-all',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
	},
	headerIconStyle: {
		display: 'flex',
		justifyContent: 'space-between',
		alignSelf: 'flex-start',
	},
	titleContainer: {
		font: token('font.heading.small'),
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		overflowWrap: 'break-word',
		display: '-webkit-box',
		lineClamp: 2,
		WebkitBoxOrient: 'vertical',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const FooterContainer = styled.div({
	flex: '1 0 auto',
	display: 'flex',
	alignItems: 'flex-end',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Footer = styled.div({
	width: '100%',
	marginTop: token('space.200'),
	display: 'flex',
	justifyContent: 'space-between',
	alignItems: 'center',
});

const CardFooter: FC = () => (
	<FooterContainer>
		<Footer>
			<LastAction />
			<SocialProof />
			<AudioPlaylistButton />
		</Footer>
	</FooterContainer>
);

type PopupViewProps = {
	shouldUseAutomationDiscovery?: boolean; // this should probably go into PageTreeContext, however since this is the only usage, it's ok to leave it here for now
	isEndOfPage?: boolean;
};

export const PopupView: FC<PopupViewProps> = ({ shouldUseAutomationDiscovery, isEndOfPage }) => {
	return (
		<PopupViewBase isEndOfPage={isEndOfPage}>
			<PageLink
				shouldHideFolder
				featureAttributionCode={isEndOfPage ? FeatureCode.EOP_REC_CARD : FeatureCode.PAGE_TREE_CARD}
			/>
			<Box padding="space.200" xcss={styles.container} testId="page-card-popup-view">
				<Stack alignBlock="start" alignInline="start" space="space.200">
					<Inline
						alignBlock="center"
						alignInline="start"
						shouldWrap={false}
						grow="fill"
						space="space.100"
					>
						<Box xcss={styles.headerIconStyle} as="span">
							<ContentIcon />
						</Box>
						<Stack xcss={styles.headerStyle} alignBlock="start" alignInline="start" grow="fill">
							<Box xcss={styles.titleContainer} as="span">
								<Title />
							</Box>
							<Subtitle />
						</Stack>
					</Inline>
				</Stack>

				<StatusLozenge isInColumn />
				<Excerpt />
				<CardFooter />
			</Box>
			<AutomationDiscoveryMessage shouldUseAutomationDiscovery={shouldUseAutomationDiscovery} />
		</PopupViewBase>
	);
};

const PopupViewBase = ({
	children,
	isEndOfPage,
}: {
	children: React.ReactNode;
	isEndOfPage?: boolean;
}) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const handlePopupClick = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				source: 'confluence',
				action: 'clicked',
				actionSubject: isEndOfPage ? 'relatedContentCard' : 'spacePageCard',
				actionSubjectId: 'excerptPopup',
				attributes: {
					source: isEndOfPage ? 'endOfPageRecommendations' : 'pageTree',
				},
			},
		}).fire();
	}, [createAnalyticsEvent, isEndOfPage]);

	return (
		<PageCardContainer isHoverPageCard onClick={handlePopupClick}>
			{children}
		</PageCardContainer>
	);
};
